
import {map} from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Dossier, CreateSLEntityDossierInput, UpdateSLEntityDossierInput } from './dossier';
import { Apollo } from 'apollo-angular';
import { Link } from './link';
import { LinkGroup } from '../link-group/link-group';
import { map as _map } from 'lodash';

const DossiersQuery = require('./gql/dossiers.query.gql');
const DossierQuery = require('./gql/dossier.query.gql');
const createDossierMutation = require('./gql/create-dossier.mutation.gql');
const deleteDossierMutation = require('./gql/delete-dossier.mutation.gql');
const archiveDossierMutation = require('./gql/archive-dossier.mutation.gql');
const unarchiveDossierMutation = require('./gql/unarchive-dossier.mutation.gql');
const createSLEntityDossierMutation = require('./gql/create-sl-entity-dossier.mutation.gql');
const updateSLEntityDossierMutation = require('./gql/update-sl-entity-dossier.mutation.gql');

const linksForGroupQuery = require('./gql/links-for-group.query.gql');
const linksByNameQuery = require('./gql/links-by-name.query.gql');

export interface DossiersQueryResult {
  dossiers: Dossier[]
  dossiersCount: number
}

export type DossierOrderBy = 'NAME';
export type DossierOrderByDirection = 'ASC' | 'DESC';

export class DossierFilter {
  offset: number
  limit: number

  search = '';
  order_by: DossierOrderBy = 'NAME';
  order_direction: DossierOrderByDirection = 'ASC';
  start_date = '0';
  end_date = '0';
  is_archived = false;
}

/**
 * Service to manage dossiers
 */
@Injectable()
export class DossierService {

  constructor(private apollo: Apollo) { }

  dossiers(filter: DossierFilter): Observable<DossiersQueryResult> {
    return this.apollo
      .query({
        query: DossiersQuery,
        variables: {
          filter
        },
        fetchPolicy: 'no-cache'
      }).pipe(
      map((result) => {
        let dossiersData = result.data['dossiers'];
        let dossiersCount = result.data['dossiersCount'];

        let dossiers = _map(dossiersData, dossierData => new Dossier(dossierData));

        return {
          dossiers,
          dossiersCount
        };
      }));
  }

  dossier(id: string): Observable<Dossier> {
    return this.apollo
      .query({
        query: DossierQuery,
        variables: {
          id
        },
        fetchPolicy: 'no-cache'
      }).pipe(
      map(result => new Dossier(result.data['dossier'])));
  }

  linksForGroup(id: string, group: LinkGroup): Observable<Link[]> {
    return this.apollo
      .query({
        query: linksForGroupQuery,
        variables: {
          id,
          filter: {
            name: group.link_name,
            group_by: [
              group.id
            ]
          }
        },
        fetchPolicy: 'no-cache'
      }).pipe(
      map(result => {
        return Dossier.createLinks(result.data['dossier'].links);
      }));
  }

  linksByName(id: string, linkName: string): Observable<Link[]> {
    return this.apollo
      .query({
        query: linksByNameQuery,
        variables: {
          id,
          filter: {
            name: linkName,
            group_by: []
          }
        },
        fetchPolicy: 'no-cache'
      }).pipe(
      map(result => {
        return Dossier.createLinks(result.data['dossier'].links);
      }));
  }

  create(dossier: Dossier): Observable<Dossier> {
    return this.apollo
      .mutate({
        mutation: createDossierMutation,
        variables: {
          input: dossier.toCreateDossierInput()
        }
      }).pipe(
      map(result => new Dossier(result.data['createDossier'])));
  }

  createSLEntity(input: CreateSLEntityDossierInput): Observable<Dossier> {
    return this.apollo
      .mutate({
        mutation: createSLEntityDossierMutation,
        variables: {
          input,
        }
      }).pipe(
      map(result => new Dossier(result.data['createSLEntityDossier'])));
  }

  updateSLEntity(id: string, input: UpdateSLEntityDossierInput): Observable<Dossier> {
    return this.apollo
      .mutate({
        mutation: updateSLEntityDossierMutation,
        variables: {
          id,
          input,
        }
      }).pipe(
      map(result => new Dossier(result.data['updateSLEntityDossier'])));
  }

  delete(id: string): Observable<string> {
    return this.apollo
      .mutate({
        mutation: deleteDossierMutation,
        variables: {
          id
        }
      }).pipe(
      map(result => result.data['deleteDossier']));
  }

  archive(id: string): Observable<Dossier> {
    return this.apollo
      .mutate({
        mutation: archiveDossierMutation,
        variables: {
          id
        }
      }).pipe(
      map(result => new Dossier(result.data['archiveDossier'])));
  }

  unarchive(id: string): Observable<Dossier> {
    return this.apollo
      .mutate({
        mutation: unarchiveDossierMutation,
        variables: {
          id
        }
      }).pipe(
      map(result => new Dossier(result.data['unarchiveDossier'])));
  }
}